import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, inject, useTemplateRef, onMounted, nextTick } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import { computedAsync } from '@vueuse/core'
    import { list_invoices, registered_parts_search, search_invoice_list_items, search_registered_parts } from '../module/invoices/module';
    import { Size } from '../components/basic_button/size';
    import Paginator from '../components/paginator.vue';
    import { VueDatePicker } from '@vuepic/vue-datepicker';
    import { vendors } from '../module/common/module';
    import _ from 'lodash'
import { BACKEND_SERVER, FRONTEND_SERVER } from '../module/common/env';
    
    interface VendorOption {
        label: string,
        value: number
    }

    interface SearchRegisteredPartsResponse {
        id: number,
        name: string
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'Reports',
  setup(__props, { expose: __expose }) {
  __expose();

    const router = useRouter()
    const is_searching_registered_parts = ref(true)
    const search_text = ref("")
    const project_id = ref("")
    const vendor = ref<VendorOption|null>(null)
    const customer = ref("")
    const project_manager = ref("")
    const invoice_NaN = ref("")
    const registered_part_name = ref("")
    const invoice_date_after = ref(null)
    const invoice_date_before = ref(null)
    const current_page_parts = ref(1)
    const current_page_invoice_line_items = ref(1)
    const page_size_parts = ref(50)
    const page_size_invoice_line_items = ref(50)

    const is_searching_registered_parts_options = [
        "Search Registered Parts",
        "Search Invoice Line Items"
    ]

    const com_is_searching_registered_parts = computed({
        get() {
            const loc_is_searching_registered_parts = is_searching_registered_parts.value
            if (loc_is_searching_registered_parts) {
                return "Search Registered Parts"
            }  else {
                return "Search Invoice Line Items"
            }
        },
        set(value: string) {
            if (value === "Search Registered Parts") {
                is_searching_registered_parts.value = true
            }  else {
                is_searching_registered_parts.value = false
            }
        }
    })
    const search_registered_parts_options = ref([] as string[])
    const search_registered_parts_value = ref([] as SearchRegisteredPartsResponse[])
    const search_registered_part_names = async function(search: string, loading: Function) {
        loading(true)
        search_registered_parts_options.value = await registered_parts_search(search, search_registered_parts_value.value.map((selected_part: SearchRegisteredPartsResponse) => selected_part.id))
        loading(false)
    }

    const com_vendor_options = computed(() => {
        return _.map(vendors, (tmp_vendor, key) => {
            return {
                "label": key,
                "value": tmp_vendor
            }
        })
    })

    const invoice_list_items = computedAsync(
        async () => {
            const vendorId = vendor.value?.value ?? 0
            const registered_part_ids = search_registered_parts_value.value.map((tmp_registered_part: SearchRegisteredPartsResponse) => {
                return tmp_registered_part.id
            })
            
            return await search_invoice_list_items(
                search_text.value,
                project_id.value,
                vendorId,
                invoice_NaN.value,
                customer.value,
                project_manager.value,
                registered_part_name.value,
                invoice_date_after.value,
                invoice_date_before.value,
                registered_part_ids,
                false,
                current_page_invoice_line_items.value,
                page_size_invoice_line_items.value,
            )
        },
        {
            results: [],
            total_count: 0
        }, /* initial state */
    )

    const registered_products = computedAsync(
        async () => {
            const vendorId = vendor.value?.value ?? 0
            const registered_part_ids = search_registered_parts_value.value.map((tmp_registered_part: SearchRegisteredPartsResponse) => {
                return tmp_registered_part.id
            })

            return await search_registered_parts(
                search_text.value,
                project_id.value,
                vendorId,
                invoice_NaN.value,
                customer.value,
                project_manager.value,
                invoice_date_after.value,
                invoice_date_before.value,
                registered_part_ids,
                false,
                current_page_parts.value,
                page_size_parts.value,
            )
        },
        {
            results: [],
            total_count: 0
        }, /* initial state */
    )

    const encodeGetParams = p => 
        Object.entries(p).map(kv => kv.map(encodeURIComponent).join("=")).join("&");


    async function download_csv() {
        const vendorId = vendor.value?.value ?? 0
        const registered_part_ids = search_registered_parts_value.value.map((tmp_registered_part: SearchRegisteredPartsResponse) => {
            return tmp_registered_part.id
        })

        if (is_searching_registered_parts.value) {
            const query_parameters = {
                search_text: search_text.value,
                project_id: project_id.value,
                vendor: vendorId,
                invoice_NaN: invoice_NaN.value,
                customer: customer.value,
                project_manager: project_manager.value,
                invoice_date_after: invoice_date_after.value,
                invoice_date_before: invoice_date_before.value,
                registered_part_ids: registered_part_ids,
                return_csv: true,
                page_number: current_page_parts.value,
                page_size: page_size_parts.value,
            }

            const url = BACKEND_SERVER + "/reports/csv/search-products?" + encodeGetParams(query_parameters);
            console.log("url", url)
            
            // Open in new tab
            window.open(url, "_blank");
        } else {
            const query_parameters = {
                search_text: search_text.value,
                project_id: project_id.value,
                vendor: vendorId,
                invoice_NaN: invoice_NaN.value,
                customer: customer.value,
                project_manager: project_manager.value,
                invoice_date_after: invoice_date_after.value,
                invoice_date_before: invoice_date_before.value,
                registered_part_ids: registered_part_ids,
                return_csv: true,
                page_number: current_page_parts.value,
                page_size: page_size_parts.value,
            }

            const url = BACKEND_SERVER + "/reports/csv/search-line-items?" + encodeGetParams(query_parameters);
            console.log("url", url)

            // Open in new tab
            window.open(url, "_blank");
        }
        
    }

    function display_date(date: string): string {
        const formatter = new Intl.DateTimeFormat("en-US")
        return formatter.format(new Date(date))
    }

    function is_numeric(str: number | string): boolean {
        if (typeof str == "number") return true
        if (typeof str != "string") return false // we only process strings!  
        return !isNaN(str as unknown as number) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
            !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
    }

    function display_currency(money: number | string): string {
        if(typeof money == "undefined"
            || money === null
            || !is_numeric(money)
        ) {
            return ""
        }

        if (typeof money === "string") {
            money = parseFloat(money)
        }

        return money.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        });
    }
// /api/parts/registered/search
// /api/invoices/items/search

const __returned__ = { router, is_searching_registered_parts, search_text, project_id, vendor, customer, project_manager, invoice_NaN, registered_part_name, invoice_date_after, invoice_date_before, current_page_parts, current_page_invoice_line_items, page_size_parts, page_size_invoice_line_items, is_searching_registered_parts_options, com_is_searching_registered_parts, search_registered_parts_options, search_registered_parts_value, search_registered_part_names, com_vendor_options, invoice_list_items, registered_products, encodeGetParams, download_csv, display_date, is_numeric, display_currency, get Size() { return Size }, Paginator, get VueDatePicker() { return VueDatePicker } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})